// packages/web-ui/src/AppShell/TabBar/TabContextMenu.tsx
//
// W2-S4 AC #5 — right-click context menu for `<TabBarItem>`. Six actions in
// fixed order (Pin/Unpin conditional). Hand-rolled (no Radix/shadcn dep
// churn) — simple absolute-positioned `<ul role="menu">` that closes on
// outside-click or Esc.

import { useCallback, useEffect, useRef } from 'react';
import type { JSX } from 'react';
import { Z_INDEX } from '../responsive-tokens.js';

export interface TabContextMenuProps {
  /** Position in viewport coords (clientX/clientY of the right-click). */
  x: number;
  y: number;
  /** Whether the target tab is pinned (toggles Pin↔Unpin item visibility). */
  pinned: boolean;
  onPinToggle: () => void;
  onClose: () => void;
  onCloseOthers: () => void;
  onCloseAllToRight: () => void;
  onCloseAll: () => void;
  /** Dismisses the menu without dispatching any action. */
  onDismiss: () => void;
}

export function TabContextMenu(props: TabContextMenuProps): JSX.Element {
  const {
    x,
    y,
    pinned,
    onPinToggle,
    onClose,
    onCloseOthers,
    onCloseAllToRight,
    onCloseAll,
    onDismiss,
  } = props;

  const rootRef = useRef<HTMLUListElement>(null);

  // Outside-click + Esc dismiss.
  useEffect(() => {
    function handleClick(event: MouseEvent): void {
      if (rootRef.current === null) return;
      if (!rootRef.current.contains(event.target as Node)) {
        onDismiss();
      }
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onDismiss();
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    // Focus the first item for keyboard a11y.
    const first = rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    first?.focus();
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onDismiss]);

  const dispatch = useCallback(
    (fn: () => void) => () => {
      fn();
      onDismiss();
    },
    [onDismiss],
  );

  return (
    <ul
      ref={rootRef}
      role="menu"
      data-testid="tab-context-menu"
      aria-label="Tab context menu"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: Z_INDEX.popover,
        background: 'var(--bg-canvas)',
        minWidth: 180,
        padding: 4,
      }}
      className="border-taupe-500 rounded-none border"
    >
      <li role="none">
        <button
          type="button"
          role="menuitem"
          data-testid={pinned ? 'ctx-unpin' : 'ctx-pin'}
          onClick={dispatch(onPinToggle)}
          className="block w-full text-left px-2 py-1 hover:bg-[var(--bg-panel-hover)]"
        >
          {pinned ? 'Unpin' : 'Pin'}
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          role="menuitem"
          data-testid="ctx-close"
          onClick={dispatch(onClose)}
          className="block w-full text-left px-2 py-1 hover:bg-[var(--bg-panel-hover)]"
        >
          Close
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          role="menuitem"
          data-testid="ctx-close-others"
          onClick={dispatch(onCloseOthers)}
          className="block w-full text-left px-2 py-1 hover:bg-[var(--bg-panel-hover)]"
        >
          Close others
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          role="menuitem"
          data-testid="ctx-close-right"
          onClick={dispatch(onCloseAllToRight)}
          className="block w-full text-left px-2 py-1 hover:bg-[var(--bg-panel-hover)]"
        >
          Close all to the right
        </button>
      </li>
      <li role="none">
        <button
          type="button"
          role="menuitem"
          data-testid="ctx-close-all"
          onClick={dispatch(onCloseAll)}
          className="block w-full text-left px-2 py-1 hover:bg-[var(--bg-panel-hover)]"
        >
          Close all
        </button>
      </li>
    </ul>
  );
}
